import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import queryKeys from "../configs/react-query/query-key";
import { PROGRESS_SBOM_STATUS } from "../constants/index";
import { useFetchData } from "./useFetchData";
import { useMutationData } from "./useMutationData";
import { useUploadFileWithGit } from "./useUploadFileWithGit";

export const useSbomVerification = (projectSeq, sbomVerificationSeq) => {
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState("");
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [sbomItemListData, setSbomItemListData] = useState([]);
  const [projectData, setProjectData] = useState({});
  const [verificationData, setVerificationData] = useState({});
  const [verificationComment, setVerificationComment] = useState("");
  const [verificationFile, setVerificationFile] = useState(null);
  const [selectedBinaryVerification, setSelectedBinaryVerification] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);

  // Show compare modal state
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [clarityVerifications, setClarityVerifications] = useState([]);
  const [clarityVerificationsLoading, setClarityVerificationsLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Confirmation modals state
  const [showRequestReVerifModal, setShowRequestReVerifModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showCompleteVerifModal, setShowCompleteVerifModal] = useState(false);
  const [showDecompleteVerifModal, setShowDecompleteVerifModal] = useState(false);
  const [sbomPage, setSbomPage] = useState(1);

  const [hasErrors, setHasErrors] = useState(true);
  const [requestData, setRequestData] = useState({});
  const [repoInfo, setRepoInfo] = useState({});

  // Add duplicate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);

  // Import the duplicate check function
  const { cloneRepositoryViaPta, cloneRepositoryViaSsh } = useUploadFileWithGit();

  // Fetch project data
  const { data: fetchedProjectData, isLoading: projectDataLoading, isFetching: projectDataFetching } = useFetchData(
    [queryKeys.getProjectByProjectId, projectSeq],
    `/project/${projectSeq}`,
    { method: "GET" },
  );

  // Fetch verification data
  const { data: fetchedVerificationData, error: fetchedVerificationDataError, isLoading: verificationDataLoading, isFetching: verificationDataFetching } = useFetchData(
    [queryKeys.getFossidById, sbomVerificationSeq],
    `/${projectSeq}/sbom_verification/${sbomVerificationSeq}`,
    { method: "GET" },
  );

  useEffect(() => {
    if (fetchedVerificationDataError) {
      if (fetchedVerificationDataError?.response?.status === 404) {
        navigate("/404", { state: { backTo: "/projects" } });
      }
    }
  }, [fetchedVerificationDataError]);

  const checkDuplicateVerification = useMutationData(
    queryKeys.checkDuplicateVerificationUpload,
    {
      method: "POST",
      params: { projectId: projectSeq, verificationType: "sbom" },
    },
    {
      onError: (error) => {
        console.error("Error during checksum verification:", error);
      },
    },
  );

  const fileGroupNo = fetchedProjectData?.fileGroupNo;
  const licensePolicySeq = fetchedProjectData?.licensePolicySeq;
  const verificationVersion = fetchedVerificationData?.verificationVersion;
  const projectId = fetchedVerificationData?.projectId;

  // Fetch license policy
  const { data: fetchedLicensePolicy, isLoading: licensePolicyLoading, isFetching: licensePolicyFetching } = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, licensePolicySeq],
    `/license_policy/${licensePolicySeq}`,
    { method: "GET" },
    {},
    { enabled: !!licensePolicySeq },
  );

  // Create a mutation for fetching clarity verifications
  const getBinaryVerificationsMutation = useMutationData(
    "binary_verification_by_project_seq",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (response && response.object) {
          setClarityVerifications(response.object);
          setTotalData(response.objectCount);
          setTotalPages(Math.ceil(response.objectCount / itemsPerPage));
        }
        setClarityVerificationsLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching binary verifications:", error);
        setClarityVerificationsLoading(false);
      },
    },
  );

  // Create a mutation for comparing SBOM with binary verification
  const getComparisonMutation = useMutationData(
    (binaryVerificationSeq) => `/sbom_verification_compare_to_binary/${sbomVerificationSeq}/${binaryVerificationSeq}`,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        setComparisonData(response);
        setComparisonLoading(false);
      },
      onError: (error) => {
        console.error("Error comparing verifications:", error);
        setComparisonLoading(false);
      },
    },
  );

  // Create a mutation for comparing SBOM with binary verification
  const downloadComparisonResultMutation = useMutationData(
    "/sbomVerificationDownloadCompareResult",
    {
      method: "GET",
      responseType: "blob",
      params: {
        sbomVerificationSeq: sbomVerificationSeq,
        binaryVerificationSeq: selectedBinaryVerification,
      },
    },
    {
      onSuccess: (response) => {
        if (response instanceof Blob) {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement("a");
          a.href = url;
          a.download = `comparison_result_${sbomVerificationSeq}_${selectedBinaryVerification}.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      },
      onError: (error) => {
        console.error("Error downloading comparison result:", error);
      },
    },
  );

  // Create a mutation for comparing SBOM with binary verification
  const downloadSbomVerificationResultMutation = useMutationData(
    "/sbomVerificationDownloadResult",
    {
      method: "GET",
      responseType: "blob",
      params: {
        sbomVerificationSeq,
      },
    },
    {
      onSuccess: (response) => {
        if (response instanceof Blob) {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement("a");
          a.href = url;
          a.download = `sbom_verification_result_${sbomVerificationSeq}.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      },
      onError: (error) => {
        console.error("Error downloading SBOM verification result:", error);
      },
    },
  );

  const { data: fetchedSbomScanComponent, isLoading: sbomScanComponentLoading, isFetching: sbomScanComponentFetching } = useFetchData(
    [queryKeys.getSourceCodeScanComponentByProjectIdAndVerificationVersion, projectId, verificationVersion],
    "/sbom_scan_component",
    {
      method: "GET",
      params: {
        projectId: projectId,
        verificationVersion: verificationVersion,
      },
    },
    {},
    { enabled: !!projectId && !!verificationVersion },
  );

  const moveToDoneMutation = useMutationData(
    "/sbom_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        if (verificationFile) {
          const formData = new FormData();
          formData.append("file", verificationFile);
          formData.append("sbomVerificationSeq", sbomVerificationSeq);
          uploadSbomVerificationFileMutation.mutate(formData);
        }
        setVerificationStatus(PROGRESS_SBOM_STATUS.DONE);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const uploadSbomVerificationFileMutation = useMutationData(
    "/sbom_verification_upload_for_verification",
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error during upload data:", error);
      },
    },
  );

  const moveToReviewMutation = useMutationData(
    "/sbom_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_SBOM_STATUS.DONE,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_SBOM_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const updateVerificationStatusToReVerificationMutation = useMutationData(
    "/sbom_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_SBOM_STATUS.REVERIFICATION);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
      },
      onError: (error) => {
        console.error("Error update verification status to re verification:", error);
      },
    },
  );

  const reUploadVerificationFileMutation = useMutationData(
    queryKeys.sbomVerificationUpload,
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("common.re-upload-file-success"));
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
      },
      onError: (error) => {
        console.error("Error during upload data:", error);
      },
    },
  );

  const addVerificationReportMutation = useMutationData(
    "/verification_report",
    { method: "POST" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("common.verification-report-added-success"));
      },
      onError: (error) => {
        console.error("Error adding verification report:", error);
      },
    },
  );

  const updateSbomVerificationMutation = useMutationData(
    "/sbom_verification",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error update verification status to re verification:", error);
      },
    },
  );

  // Update project data when fetched
  useEffect(() => {
    if (fetchedProjectData) {
      setProjectData(fetchedProjectData);
    }
  }, [fetchedProjectData]);

  // Update verification data when fetched
  useEffect(() => {
    if (fetchedVerificationData) {
      setVerificationData(fetchedVerificationData);
      setVerificationStatus(fetchedVerificationData.verificationStatusCd);
      setVerificationComment(fetchedVerificationData.verificationComment);
      setVerificationFile(fetchedVerificationData.verificationRealFileName);
    }
  }, [fetchedVerificationData]);

  // Function to fetch clarity verifications
  const fetchClarityVerifications = () => {
    setClarityVerificationsLoading(true);
    const requestBody = {
      pageSize: itemsPerPage,
      startRow: itemsPerPage * (currentPage - 1),
      projectSeq: projectSeq,
    };
    getBinaryVerificationsMutation.mutate(requestBody);
  };

  // Function to handle page change in clarity verifications table
  const handleClarityVerificationPageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Function to handle items per page change in clarity verifications table
  const handleClarityVerificationItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Effect to fetch clarity verifications when page or items per page changes
  useEffect(() => {
    if (showCompareModal) {
      fetchClarityVerifications();
    }
  }, [currentPage, itemsPerPage, showCompareModal]);

  // Function to select a clarity verification for comparison
  const selectClarityVerification = (verification) => {
    setSelectedBinaryVerification(verification);
    setComparisonLoading(true);
    getComparisonMutation.mutate(verification.binaryVerificationSeq);
  };

  const handleClose = () => {
    navigate(`/projects/${projectSeq}`, { state: { activeTab: "sbom" } });
  };

  // Open confirmation modal first instead of directly changing state
  const handleRequestVerificationClick = () => {
    if (verificationStatus === PROGRESS_SBOM_STATUS.REVERIFICATION) {
      setShowRequestModal(true);
    } else {
      setShowRequestReVerifModal(true);
    }
  };

  // Actual state change after confirmation
  const handleRequestVerification = () => {
    // If we're currently in the ON_RE_VERIF state, transition to ON_REVIEW when Request button is clicked
    if (verificationStatus === PROGRESS_SBOM_STATUS.REVERIFICATION) {
      setVerificationStatus(PROGRESS_SBOM_STATUS.REVIEW);
      setShowRequestModal(false);
    } else {
      // If we're in any other state, transition to ON_RE_VERIF (requesting re-verification)
      setVerificationStatus(PROGRESS_SBOM_STATUS.REVERIFICATION);
      setShowRequestReVerifModal(false);
    }
    // API call will be implemented later
  };

  // Open confirmation modal first
  const handleCompleteVerificationClick = () => {
    setShowCompleteVerifModal(true);
  };

  const handleMoveToDone = () => {
    return moveToDoneMutation.mutateAsync({
      sbomVerificationSeq: sbomVerificationSeq,
      verificationComment: verificationComment || "",
      verificationStatusCd: PROGRESS_SBOM_STATUS.DONE,
    });
  };

  // Open confirmation modal first
  const handleDecompleteVerificationClick = () => {
    setShowDecompleteVerifModal(true);
  };

  // Actual state change after confirmation
  const handleDecompleteFromDone = () => {
    return moveToReviewMutation.mutateAsync({
      sbomVerificationSeq: sbomVerificationSeq,
      verificationStatusCd: PROGRESS_SBOM_STATUS.REVIEW,
    });
  };

  const handleCompareWithClarity = () => {
    setShowCompareModal(true);
    if (clarityVerifications.length === 0) {
      fetchClarityVerifications();
    }
  };

  const handleReUploadFile = () => {
    // Show file upload modal or trigger file upload functionality
    setShowFileUploadModal(true);
    // API call will be implemented later
  };

  // Updated handleConfirmFileUpload with duplicate check
  const handleConfirmFileUpload = async () => {
    if (Object.keys(repoInfo).length !== 0) {
      if (repoInfo.token) {
        await cloneRepositoryViaPta({ repoInfo, requestData });
      } else {
        await cloneRepositoryViaSsh({ repoInfo, requestData });
      }
    }
    if (requestData && requestData.verifFile && requestData.verifFile[0]) {
      try {
        // Check for duplicate file first
        const checksumFormData = new FormData();
        checksumFormData.append("file", requestData.verifFile[0]);

        const response = await checkDuplicateVerification.mutateAsync(checksumFormData);
        const duplicatedSeq = response?.duplicateSeq || 0;;
        if (duplicatedSeq > 0) {
           setPendingUpload(() => () => {
             handleConfirmFileUploadOriginal();
           });
          setShowDuplicateModal(true);
        } else {
          // No duplicate, proceed with upload
          handleConfirmFileUploadOriginal();
        }
      } catch (error) {
        console.error("Error checking duplicate file:", error);
        // If check fails, proceed with upload anyway
        handleConfirmFileUploadOriginal();
      }
    }
  };

  // Original file upload logic (renamed)
  const handleConfirmFileUploadOriginal = () => {
    const { verifFile, requestComment, sbomSeq, scanTypeCd } = requestData;
    const formData = new FormData();
    formData.append("sbomFile", verifFile[0]);
    formData.append("sbomSeq", sbomSeq);
    formData.append("sbomVerificationSeq", sbomVerificationSeq);
    reUploadVerificationFileMutation.mutate(formData);
    updateSbomVerificationMutation.mutate({
      sbomVerificationSeq,
      requestComment,
      scanTypeCd,
      projectSeq,
      projectId,
      verificationVersion,
    });
    setShowFileUploadModal(false);
  };

  // Duplicate modal handlers
  const handleDuplicateModalClose = () => {
    setShowDuplicateModal(false);
    setPendingUpload(null);
  };

  const handleDuplicateConfirm = () => {
    if (pendingUpload) {
      pendingUpload();
    }
    handleDuplicateModalClose();
  };

  const handleRequestReVerification = (dueDate) => {
    return updateVerificationStatusToReVerificationMutation.mutateAsync({
      sbomVerificationSeq: sbomVerificationSeq,
      verificationStatusCd: PROGRESS_SBOM_STATUS.REVERIFICATION,
      reverificationDueDt: dueDate ? dueDate.toISOString() : null,
    });
  };

  // Handler for adding verification report
  const handleAddVerificationReport = (reportData) => {
    addVerificationReportMutation.mutate(reportData);
  };

  // Handler for downloading comparison result
  const handleDownloadComparisonResult = () => {
    if (selectedBinaryVerification) {
      downloadComparisonResultMutation.mutate();
    }
  };

  const handleDownloadSbomVerificationResult = () => {
    downloadSbomVerificationResultMutation.mutate();
  };

  // Combined loading state for all essential API calls
  // Check both isLoading (no data in cache) and isFetching (refetching data)
  const isLoading = 
    projectDataLoading ||
    verificationDataLoading ||
    licensePolicyLoading ||
    sbomScanComponentLoading;

  const isFetching = 
    projectDataFetching ||
    verificationDataFetching ||
    licensePolicyFetching ||
    sbomScanComponentFetching;

  return {
    // Loading state
    isLoading,
    isFetching,

    // Data
    verificationStatus,
    sbomItemListData,
    projectData,
    fetchedLicensePolicy,
    fetchedSbomScanComponent,
    verificationData,
    verificationComment,
    verificationFile,
    comparisonData,
    requestData,
    hasErrors,

    // Clarity verification comparison
    clarityVerifications,
    clarityVerificationsLoading,
    comparisonLoading,
    currentPage,
    itemsPerPage,
    totalPages,
    totalData,
    selectedBinaryVerification,

    // Modal state
    showFileUploadModal,
    showRequestReVerifModal,
    showRequestModal,
    showCompleteVerifModal,
    showDecompleteVerifModal,
    showCompareModal,
    showDuplicateModal,

    // Modal controls
    setShowFileUploadModal,
    setShowRequestReVerifModal,
    setShowRequestModal,
    setShowCompleteVerifModal,
    setShowDecompleteVerifModal,
    setShowCompareModal,
    setVerificationComment,
    setVerificationFile,
    setHasErrors,
    setRequestData,
    setRepoInfo,

    // Table actions
    handleClarityVerificationPageChange,
    handleClarityVerificationItemsPerPageChange,
    selectClarityVerification,

    // Actions
    handleClose,
    handleRequestVerificationClick,
    handleRequestVerification,
    handleCompleteVerificationClick,
    handleMoveToDone,
    handleDecompleteVerificationClick,
    handleDecompleteFromDone,
    handleCompareWithClarity,
    handleReUploadFile,
    handleConfirmFileUpload,
    handleRequestReVerification,
    handleAddVerificationReport,
    handleDownloadComparisonResult,
    handleDownloadSbomVerificationResult,

    // Duplicate handling
    handleDuplicateModalClose,
    handleDuplicateConfirm,
  };
};
